import React from 'react';
import { Link, LinkProps, To } from 'react-router-dom';
import { getCurrentLocale } from '../i18n';

// A wrapper around Link that preserves the locale in the URL
const LocalizedLink: React.FC<LinkProps> = ({ to, children, ...rest }) => {
  const currentLocale = getCurrentLocale();

  // Only add locale prefix for non-English locales
  const localePrefix = currentLocale === 'en' ? '' : `/${currentLocale}`;

  // Handle different types of 'to' prop
  let localizedTo: To = to;

  if (typeof to === 'string') {
    // Split the URL into path and query parts
    const [path, query] = to.split('?');
    const queryPart = query ? `?${query}` : '';

    // If it's already a localized path, don't modify it
    if (path.startsWith(`/${currentLocale}/`) || path === `/${currentLocale}`) {
      localizedTo = to;
    }
    // If it's the root path and we're not in English
    else if (path === '/' && currentLocale !== 'en') {
      localizedTo = `${localePrefix}${queryPart}`;
    }
    // For all other paths, add the locale prefix if needed
    else if (currentLocale !== 'en' && !path.startsWith(localePrefix)) {
      // Make sure we don't double-slash
      const cleanPath = path.startsWith('/') ? path : `/${path}`;
      localizedTo = `${localePrefix}${cleanPath}${queryPart}`;
    } else {
      localizedTo = to;
    }
  } else if (typeof to === 'object' && to !== null) {
    // Handle object 'to' props (like { pathname, search })
    const toObj = { ...to };

    if (toObj.pathname) {
      // If it's already a localized path, don't modify it
      if (toObj.pathname.startsWith(`/${currentLocale}/`) || toObj.pathname === `/${currentLocale}`) {
        // No change needed
      }
      // If it's the root path and we're not in English
      else if (toObj.pathname === '/' && currentLocale !== 'en') {
        toObj.pathname = localePrefix;
      }
      // For all other paths, add the locale prefix if needed
      else if (currentLocale !== 'en' && !toObj.pathname.startsWith(localePrefix)) {
        // Make sure we don't double-slash
        const cleanPath = toObj.pathname.startsWith('/') ? toObj.pathname : `/${toObj.pathname}`;
        toObj.pathname = `${localePrefix}${cleanPath}`;
      }
    }

    localizedTo = toObj;
  }
  // For other types of 'to' props, we don't modify them

  return <Link to={localizedTo} {...rest}>{children}</Link>;
};

export default LocalizedLink;
